'use client';

import { LogoText } from '@ahamove/icons';
import { Bike, Truck } from '@ahamove/icons/home-service';
import {
  Balance,
  Challenge,
  ConfirmOrder,
  DataVisual,
  DraftOrder,
  FavDriver,
  HelpCenter,
  Merchant,
  Nationwide,
  News,
  ParentAccount,
  Records,
  Referral,
  Rewards,
  RoDelivery,
  RoPickUp,
  Statistic,
  Subscription,
  Warehouse,
} from '@ahamove/icons/pages';
import {
  COOKIE_NAME__ACCESS_TOKEN,
  COOKIE_NAME__REFRESH_TOKEN,
  NEED_CONFIRM_PICKUP_PARTNERS,
  USER_TAG,
  // apiRoutes,
  deleteCookie,
} from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useAuth, useMyProfile } from '@ahm/react-auth';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CloseButton,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  rem,
} from '@mantine/core';
// import { useQueryClient } from '@tanstack/react-query';
import { AlignJustify, ChevronRight, UserRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  useEffect,
  useMemo,
  type JSX,
  type ReactNode,
  type SVGProps,
} from 'react';
import classes from './booking-layout.module.css';
import IconHouseMoving from '@/assets/icons/house-moving-icon.svg';
import RoSmeIcon from '@/assets/icons/ro-sme-icon.svg';
import { publicEnv } from '@/config/public-env.config.mjs';
import { ROUTES } from '@/config/router';
import { useGetOrderList } from '@/features/booking/api/order/use-get-order-list';
import { PAGE_SIZE } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { getServiceType } from '@/lib/utils';
import { Link, usePathname, useRouter } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type NavItem = {
  name: string;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element | ReactNode;
  activeIcon?: string;
  title: string;
  route?: string;
  url?: string;
  external_url?: string;
  color?: string;
  hidden?: boolean;
  isNew?: boolean;
};

export default function BookingNavbar() {
  const pathname = usePathname();
  const { isBikeService, isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const { session } = useAuth();
  const user = session?.user;
  const isMobile = useIsMobile();
  const tCommon = useTranslations('common');
  const { isExpandedNavbar, setIsExpandedNavbar, setLastOrderService } =
    useBookingStore((state) => state);

  const paramOrderList = {
    total: true,
    offset: 0,
    count: PAGE_SIZE,
  };

  const isRecordsPage = pathname === ROUTES.records;

  const { data: orderList } = useGetOrderList(paramOrderList, {
    enabled: !isTruckService && !isRecordsPage,
  });
  useEffect(() => {
    if (isBikeService && orderList?.orders?.length > 0) {
      const lastBikeOrder = orderList?.orders?.find(
        (order) =>
          order.delivery_type !== 'TRUCK' &&
          order.delivery_type !== 'NATIONWIDE'
      );
      if (lastBikeOrder) {
        setLastOrderService(lastBikeOrder);
      }
    }
  }, [isBikeService, orderList]);

  const handleToggleNavbar = () => {
    setIsExpandedNavbar(!isExpandedNavbar);
  };

  // Close mobile navbar when clicking outside or on menu item
  const handleClickOutside = () => {
    if (isMobile && isExpandedNavbar) {
      setIsExpandedNavbar(false);
    }
  };

  const isRoSme = useMemo(() => {
    return !!user?.tags?.includes('SME_RO') && !user?.saas;
  }, [user]);

  const roRoute: NavItem = useMemo(() => {
    const defaultRoRouteOptions = {
      hidden: !user || !user?.verified || isMobile,
      name: 'ro_tool',
      icon: RoDelivery,
      title: 'page.ro_tool',
    };

    if (isRoSme) {
      return {
        ...defaultRoRouteOptions,
        route: `${publicEnv.NEXT_PUBLIC_APP_URI}${ROUTES.roSme}`,
        name: 'ro_sme',
        icon: RoSmeIcon,
        title: 'page.ro_sme',
      };
    }

    return {
      ...defaultRoRouteOptions,
      route: ROUTES.roTool,
    };
  }, [isRoSme, user, isMobile]);

  const newRoRoute: NavItem = useMemo(() => {
    const defaultNewRoRouteOptions = {
      hidden: !user || !user?.verified || isMobile,
      name: 'new_ro',
      icon: RoDelivery,
      title: 'page.ro_tool',
    };

    if (isRoSme) {
      return {
        ...defaultNewRoRouteOptions,
        route: `${publicEnv.NEXT_PUBLIC_APP_URI}${ROUTES.ro}`,
        name: 'new_ro_sme',
        icon: RoSmeIcon,
        title: 'page.ro_sme',
        isNew: true,
      };
    }

    return {
      ...defaultNewRoRouteOptions,
      route: `${publicEnv.NEXT_PUBLIC_APP_URI}${ROUTES.ro}`,
    };
  }, [isRoSme, user, isMobile]);

  useEffect(() => {
    document.documentElement.classList.toggle('ahm__nBeXpED', isExpandedNavbar);

    return () => {
      document.documentElement.classList.remove('ahm__nBeXpED');
    };
  }, [isExpandedNavbar]);

  const sidebarNavItems: NavItem[] = [
    {
      icon: Bike,
      title: 'page.new_order',
      // icon: 'Home',
      // activeIcon: 'HomeActive',
      // title: 'page.home',
      route: ROUTES.home,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}`,
      name: 'new_order',
    },
    {
      icon: Nationwide,
      title: 'page.nationwide',
      route: ROUTES.nationwide,
      name: 'nationwide',
      hidden: isMobile,
    },
    {
      icon: Truck,
      title: 'page.truck',
      route: ROUTES.truck,
      name: 'truck',
      hidden: isMobile,
    },
    {
      icon: IconHouseMoving,
      title: 'page.house_moving',
      route: ROUTES.houseMoving,
      name: 'house_moving',
      isNew: true,
      hidden: isMobile,
    },
    // {
    //   icon: 'EVRental',
    //   title: 'page.ev_rental',
    //   name: 'ev_rental',
    //   hidden: isMobile,
    //   external_url: publicEnv.NEXT_PUBLIC_EV_URI,
    // },
    {
      icon: Records,
      // icon: 'Clock',
      // activeIcon: 'ClockActive',
      title: 'page.records',
      route:
        publicEnv.NEXT_PUBLIC_APP_ENV === 'production'
          ? undefined
          : ROUTES.records,
      url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/records`,
      name: 'records',
    },
    {
      icon: ConfirmOrder,
      title: 'page.verify_order',
      route: ROUTES.verifyOrder,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/verify-order`,
      color: 'accent',
      name: 'verify-order',
      hidden:
        !user?.partner ||
        NEED_CONFIRM_PICKUP_PARTNERS.indexOf(user?.partner) == -1,
    },
    {
      icon: DraftOrder,
      title: 'page.draft_order',
      route: ROUTES.draftOrder,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/draft-order`,
      name: 'draft_order',
    },
    {
      icon: FavDriver,
      title: 'page.favorite_drivers',
      route: ROUTES.favoriteDrivers,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/favorite-drivers`,
      name: 'favorite_drivers',
    },
    {
      icon: Balance,
      title: 'page.user_balance',
      route: ROUTES.balance,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/balance`,
      name: 'balance',
    },
    {
      icon: Subscription,
      title: 'page.subscriptions',
      route: ROUTES.subscriptions,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/subscriptions`,
      name: 'subscriptions',
    },
    roRoute,
    newRoRoute,
    // {
    //   icon: RoDelivery,
    //   title: 'page.ro_tool',
    //   route: !user?.partner
    //     ? ROUTES.roTool
    //     : `${publicEnv.NEXT_PUBLIC_APP_V1_URI}${ROUTES.roTool}`,
    //   name: 'ro_tool',
    //   hidden: !user || !user?.verified || isMobile,
    // },
    {
      icon: RoPickUp,
      title: 'page.route_optimization_pickup',
      route: ROUTES.routeOptimizationPickup,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/route-optimization-pickup`,
      color: 'accent',
      name: 'route-optimization-pickup',
      hidden:
        !user?.verified ||
        !user.max_stop_points ||
        user.max_stop_points < 5 ||
        isMobile,
    },
    {
      icon: Rewards,
      title: 'page.rewards',
      name: 'rewards',
      route: ROUTES.rewards,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/rewards`,
    },
    {
      icon: Challenge,
      title: 'page.challenges',
      route: ROUTES.challenges,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/challenges`,
      name: 'challenges',
      hidden: true,
    },
    {
      icon: News,
      title: 'page.news',
      route: ROUTES.news,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/info`,
      name: 'news',
    },
    // {
    //   icon: 'Notification',
    //   activeIcon: 'NotificationActive',
    //   title: 'page.notifications',
    //   route: '/notifications',
    //   name: 'notifications',
    // },
    {
      icon: Statistic,
      title: 'page.statistics',
      route: ROUTES.statistics,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/statistics`,
      name: 'statistics',
      // hidden: isMobile,
    },
    {
      icon: ParentAccount,
      title: 'page.employees',
      route: ROUTES.employees,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/employees`,
      name: 'employees',
    },
    {
      icon: Referral,
      title: 'page.referral',
      route: ROUTES.referral,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/invite`,
      name: 'referral',
    },
    {
      icon: Warehouse,
      title: 'page.warehouse',
      route: ROUTES.warehouse,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/warehouse`,
      name: 'warehouse',
      hidden:
        !user?.verified ||
        !user?.partner ||
        !user?.tags?.includes(USER_TAG.RO_EXPERT) ||
        isMobile,
    },
    {
      icon: HelpCenter,
      title: 'page.help',
      route: ROUTES.helpCenter,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/help-center`,
      name: 'help',
    },
    {
      icon: Merchant,
      title: 'page.merchant',
      url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/v2/merchant`,
      // route : '/help-center',
      color: 'accent',
      name: 'Merchant Confirm Order',
      hidden: !user?.tags?.includes(USER_TAG.MERCHANT_GOSHOP),
    },
    {
      icon: DataVisual,
      title: 'page.audience',
      route: ROUTES.audience,
      // url: `${publicEnv.NEXT_PUBLIC_APP_V1_URI}/audience`,
      name: 'audience',
      // hidden: isMobile && !user?.verified,
    },
  ];

  return (
    <>
      <style>
        {`.ahm__nBeXpED:root{--booking-layout-navbar-width:18.75rem}@media (min-width:768px){:root{--booking-layout-navbar-width:4rem;--booking-layout-navbar-offset:var(--booking-layout-navbar-width, 0px)}}`}
      </style>
      {isMobile && isExpandedNavbar && (
        <div
          onClick={handleClickOutside}
          onKeyDown={(e) => e.key === 'Escape' && handleClickOutside()}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1090,
          }}
        />
      )}
      <aside
        className={cn(
          'inset-y z-20 flex flex-col',
          // 'border-t-8 border-t-secondary-60 border-solid border-b-0 border-x-0',
          classes.navbar,
          { [classes.navbarMobileExpanded]: isExpandedNavbar && isMobile }
        )}
      >
        {isExpandedNavbar ? (
          <Stack
            style={{
              background:
                'linear-gradient(106.41deg,#ffa242 -5.47%,#ff8302 129.9%)',
            }}
            gap={0}
          >
            <Group justify="space-between" py={4} px={16}>
              <LogoText className="aspect-[521/90] h-auto w-20" />
              <CloseButton
                mr={-8}
                c="white"
                size="lg"
                bg="transparent"
                onClick={handleToggleNavbar}
              />
            </Group>
            <UserProfile />
            <Group justify="end" align="center" py={9} px={12}>
              <Button
                pr={0}
                fw={500}
                size="compact-xs"
                c="white"
                component={Link}
                href="/membership"
                variant="subtle"
              >
                {tCommon('member')}
                <ChevronRight spacing={0} size={20} />
              </Button>
            </Group>
          </Stack>
        ) : (
          <div className="flex flex-row flex-nowrap items-center justify-between gap-4 px-2.5 py-2">
            <Button
              variant="transparent"
              color="neutral"
              flex="none"
              h={rem(44)}
              w={rem(44)}
              p={rem(10)}
              classNames={{
                root: classes.navbarBtn,
              }}
              onClick={handleToggleNavbar}
            >
              <AlignJustify className="size-6" />
            </Button>
          </div>
        )}

        <ScrollArea scrollbars="y">
          <nav
            className={cn('grid gap-1 px-2.5 pb-8 pt-2.5', {
              'gap-2': isExpandedNavbar,
            })}
          >
            {sidebarNavItems.map((item) => {
              if (item.hidden) return null;

              return (
                <BookingNavbarItem
                  key={item.name}
                  item={item}
                  isExpanded={isExpandedNavbar}
                />
              );
            })}
          </nav>
        </ScrollArea>
      </aside>
    </>
  );
}

export type BookingNavbarItemProps = {
  item: NavItem;
  isExpanded: boolean;
};

export function BookingNavbarItem({
  item,
  isExpanded,
}: BookingNavbarItemProps) {
  const tCommon = useTranslations('common');
  const Icon = item.icon;
  const pathname = usePathname();

  const { setEstimatedData, setIsExpandedNavbar } = useBookingStore(
    (state) => state
  );
  const isMobile = useIsMobile();

  return withTooltip(
    <Button
      component={Link}
      href={item.route ?? item.url ?? item.external_url ?? '/'}
      target={item.external_url ? '_blank' : undefined}
      h={rem(44)}
      onClick={() => {
        if (item.route === ROUTES.nationwide) {
          setEstimatedData(null);
        }
        // Close navbar on mobile when clicking on menu item
        if (isMobile && isExpanded) {
          setIsExpandedNavbar(false);
        }
      }}
      w={isExpanded ? '100%' : rem(44)}
      p={10}
      variant="subtle"
      color="neutral"
      aria-label={tCommon(item.title as any)}
      leftSection={<Icon className="size-6" />}
      rightSection={
        item.isNew && isExpanded ? (
          <Badge mr={rem(-12)} tt="capitalize">
            {tCommon('new')}
          </Badge>
        ) : null
      }
      classNames={{
        root: classes.navbarBtn,
      }}
      className="!important hover:bg-violet-600"
      styles={{
        root: {
          backgroundColor: pathname === item.route ? 'white' : 'transparent',
        },
        inner: {
          justifyContent: isExpanded ? 'flex-start' : 'center',
          position: 'relative',
        },
        label: {
          marginRight: isExpanded ? 'auto' : 0,
        },
        section: {
          marginRight: isExpanded ? rem(12) : 0,
        },
      }}
    >
      {!isExpanded && item.isNew ? (
        <Badge
          tt="capitalize"
          pos="absolute"
          size="xs"
          top={rem(-8)}
          right={rem(-8)}
          h={rem(12)}
          lh={1.5}
        >
          {tCommon('new')}
        </Badge>
      ) : null}
      {isExpanded ? tCommon(item.title as any) : null}
    </Button>,
    tCommon(item.title as any),
    !isExpanded
  );
}
export function UserProfile() {
  const tCommon = useTranslations('common');
  const router = useRouter();

  const { session } = useAuth();
  const user = session?.user;

  const { isFetching } = useMyProfile();
  // const queryClient = useQueryClient();
  // const queryState = queryClient.getQueryState([apiRoutes.user.profile]);
  // const queryStatus = queryState?.status;

  const handleClickSignIn = () => {
    deleteCookie(COOKIE_NAME__ACCESS_TOKEN, process.env.NEXT_PUBLIC_DOMAIN);
    deleteCookie(COOKIE_NAME__REFRESH_TOKEN, process.env.NEXT_PUBLIC_DOMAIN);
    const { origin, pathname, search } = window.location;
    let redirectUrl = `${origin}${pathname}${search}`;
    if (window.encodeURIComponent)
      redirectUrl = window.encodeURIComponent(redirectUrl);
    router.push(
      `${process.env.NEXT_PUBLIC_ACCOUNTS_URI}/sign-in?redirect_uri=${redirectUrl}`
    );
  };

  if (isFetching)
    return (
      <Card
        radius="md"
        py={rem(8)}
        px={rem(12)}
        mx={rem(16)}
        bg="rgba(255,255,255,0.2)"
      >
        <Group my="auto">
          <Skeleton opacity={0.35} h={48} w={48} radius={100} />
          <Stack flex={1} ta="left" gap={4}>
            <Skeleton opacity={0.35} h={16} w={120} />
            <Skeleton opacity={0.35} h={16} w={80} />
          </Stack>
        </Group>
      </Card>
    );

  if (user)
    return (
      <Button
        py={rem(8)}
        px={rem(12)}
        mx={rem(16)}
        bg="rgba(255,255,255,0.2)"
        h="auto"
        component={Link}
        href="/profile"
        variant="subtle"
        radius="md"
        classNames={{
          root: classes.navbarInfoBtn,
        }}
        leftSection={
          <Avatar
            variant="outline"
            size={rem(48)}
            src={
              user?.avatar ??
              (user?.facebook_id
                ? 'https://graph.facebook.com/' +
                  user.facebook_id +
                  '/picture?type=normal'
                : undefined)
            }
            alt={user.name}
            styles={{
              root: {
                border: '2px solid white',
                backgroundColor: 'white',
              },
              placeholder: {
                border: 'none',
                backgroundColor: 'white',
                color: 'var(--mantine-color-primary-4)',
              },
            }}
          >
            <UserRound size={28} />
          </Avatar>
        }
        styles={{
          root: {
            border: 'none',
          },
          inner: {
            justifyContent: 'flex-start',
          },
          section: {
            marginRight: rem(12),
          },
        }}
      >
        <Stack flex={1} ta="left" gap={0}>
          <Text size="sm" c="white" lh="h6" fw={600} maw={180} truncate>
            {user.name}
          </Text>
          {user.nickname ? (
            <Text size="xs" fw={500} c="white" maw={180} truncate>
              {user.nickname}
            </Text>
          ) : null}
          {user._id ? (
            <Text size="sm" fw={500} c="white" maw={180} truncate>
              +{user._id}
            </Text>
          ) : null}
        </Stack>
      </Button>
    );
  return (
    <Button
      py={rem(8)}
      px={rem(12)}
      mx={rem(16)}
      bg="rgba(255,255,255,0.2)"
      h="auto"
      onClick={handleClickSignIn}
      variant="subtle"
      radius="md"
      classNames={{
        root: classes.navbarInfoBtn,
      }}
      leftSection={
        <Avatar
          variant="outline"
          size={rem(48)}
          alt={'Ahamove'}
          styles={{
            root: {
              border: '2px solid white',
              backgroundColor: 'white',
            },
            placeholder: {
              border: 'none',
              backgroundColor: 'white',
              color: 'var(--mantine-color-primary-4)',
            },
          }}
        >
          <UserRound size={28} />
        </Avatar>
      }
      styles={{
        root: {
          border: 'none',
        },
        inner: {
          justifyContent: 'flex-start',
        },
        section: {
          marginRight: rem(12),
        },
      }}
    >
      <Title
        order={6}
        lineClamp={3}
        size="lg"
        c="white"
        lh="h6"
        fw={700}
        maw={180}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {tCommon('page.sign-in')}
      </Title>
    </Button>
  );
}

function withTooltip(Component: ReactNode, label: string, enable: boolean) {
  return enable ? (
    <Tooltip label={label} position="right">
      {Component}
    </Tooltip>
  ) : (
    Component
  );
}
